<script setup>
import { computed, ref } from 'vue';
import { useMutationObserver } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';

const { t } = useI18n();
const isDark = ref(document.body.classList.contains('dark'));
const SCENE = '/branding/guandalini/theme-scene-v1.jpg';
const THEME_ICONS = '/branding/guandalini/theme-icons-recorte-v1.webp';

useMutationObserver(
  document.body,
  () => {
    isDark.value = document.body.classList.contains('dark');
  },
  { attributes: true, attributeFilter: ['class'] }
);

const themeLabel = computed(() =>
  isDark.value
    ? t('PROFILE_THEME_SWITCH.DARK')
    : t('PROFILE_THEME_SWITCH.LIGHT')
);
const actionLabel = computed(() =>
  isDark.value
    ? t('PROFILE_THEME_SWITCH.SWITCH_TO_LIGHT')
    : t('PROFILE_THEME_SWITCH.SWITCH_TO_DARK')
);

const toggleTheme = () => {
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.COLOR_SCHEME,
    isDark.value ? 'light' : 'dark'
  );
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
  isDark.value = document.body.classList.contains('dark');
};
</script>

<template>
  <li class="n-dropdown-item">
    <div
      class="group relative isolate flex min-h-[4.125rem] w-full items-center overflow-hidden rounded-lg px-3 py-2"
      :class="isDark ? 'text-guandalini-warm' : 'text-guandalini-graphite'"
    >
      <span
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 -z-10 overflow-hidden transition-transform duration-500 group-hover:scale-[1.025] motion-reduce:transform-none motion-reduce:transition-none"
      >
        <span
          class="absolute inset-0 overflow-hidden transition-opacity duration-500 motion-reduce:transition-none"
          :class="isDark ? 'opacity-0' : 'opacity-100'"
        >
          <img
            :src="SCENE"
            alt=""
            draggable="false"
            class="absolute inset-x-0 top-0 h-[200%] w-full max-w-none select-none object-fill rtl:-scale-x-100"
          />
        </span>
        <span
          class="absolute inset-0 overflow-hidden transition-opacity duration-500 motion-reduce:transition-none"
          :class="isDark ? 'opacity-100' : 'opacity-0'"
        >
          <img
            :src="SCENE"
            alt=""
            draggable="false"
            class="absolute inset-x-0 top-0 h-[200%] w-full max-w-none -translate-y-1/2 select-none object-fill rtl:-scale-x-100"
          />
        </span>
      </span>
      <span
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 -z-10 from-[0%] via-[25%] to-transparent to-[78%] ltr:bg-gradient-to-r rtl:bg-gradient-to-l"
        :class="
          isDark
            ? 'from-guandalini-graphite/95 via-guandalini-graphite/85'
            : 'from-guandalini-warm/95 via-guandalini-warm/85'
        "
      />
      <div class="flex items-center gap-2.5">
        <span class="flex flex-col gap-0.5">
          <span class="text-sm leading-4">{{
            t('PROFILE_THEME_SWITCH.TITLE')
          }}</span>
          <span class="text-xs leading-3 opacity-80">{{ themeLabel }}</span>
        </span>
        <button
          type="button"
          role="switch"
          :aria-checked="isDark"
          :aria-label="actionLabel"
          class="relative size-11 shrink-0 cursor-pointer rounded-full transition-transform duration-200 hover:-translate-y-0.5 motion-reduce:transform-none motion-reduce:transition-none"
          @click.stop="toggleTheme"
        >
          <span
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 overflow-hidden transition-opacity duration-300 motion-reduce:transition-none"
            :class="isDark ? 'opacity-0' : 'opacity-100'"
          >
            <img
              :src="THEME_ICONS"
              alt=""
              draggable="false"
              class="absolute top-0 h-full w-[200%] max-w-none select-none object-fill ltr:end-0 rtl:start-0"
            />
          </span>
          <span
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 overflow-hidden transition-opacity duration-300 motion-reduce:transition-none"
            :class="isDark ? 'opacity-100' : 'opacity-0'"
          >
            <img
              :src="THEME_ICONS"
              alt=""
              draggable="false"
              class="absolute top-0 h-full w-[200%] max-w-none select-none object-fill ltr:start-0 rtl:end-0"
            />
          </span>
        </button>
      </div>
    </div>
  </li>
</template>
